import { c as _c } from "react/compiler-runtime";
// Выдача поиска по запросу, похожему на цифровой UGC-артикул (ТЗ 31.10.2026).
//
// - Активный артикул (резолв или `ugcRedirect` в ответе поиска - прямая
//   ссылка /search?q=12345678) - сразу карточка товара, адрес выдачи заменяется.
// - Нет такого или отключён - над выдачей сообщение «Товар по артикулу не
//   найден»; если обычный поиск по этим цифрам пуст, вместо пустого экрана -
//   популярное из каталога.
import { useQuery } from "@tanstack/react-query";
import { UGC_TEXTS } from "@osmix/contracts/ugc-articles";
import { SearchX } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { api } from "@/api";
import { ProductGrid, ProductGridSkeleton } from "@/components/product/product-grid";
import { DiscoverySourceProvider } from "@/lib/discovery-source";
import { enterUgcArticle, looksLikeUgcArticle, resolveUgcCode, ugcRedirectOf, ugcResolveKey } from "@/lib/ugc-search";
import { usePreferences } from "@/store/preferences";
export type UgcSearchState = "none" | "checking" | "redirecting" | "notFound";
export function useUgcSearch(search, redirect) {
  const $ = _c(18);
  const navigate = useNavigate();
  let code;
  let isCode;
  let t0;
  let t1;
  if ($[0] !== search) {
    code = search?.trim() ?? "";
    isCode = looksLikeUgcArticle(code);
    t1 = useQuery;
    t0 = ugcResolveKey(code);
    $[0] = search;
    $[1] = code;
    $[2] = isCode;
    $[3] = t0;
    $[4] = t1;
  } else {
    code = $[1];
    isCode = $[2];
    t0 = $[3];
    t1 = $[4];
  }
  let t2;
  if ($[5] !== code) {
    t2 = () => resolveUgcCode(code);
    $[5] = code;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== isCode || $[8] !== t0 || $[9] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t2,
      enabled: isCode,
      staleTime: 60000
    };
    $[7] = isCode;
    $[8] = t0;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const resolved = t1(t3);
  let t4;
  if ($[11] !== isCode || $[12] !== redirect) {
    t4 = isCode ? ugcRedirectOf(redirect) : null;
    $[11] = isCode;
    $[12] = redirect;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const fromSearch = t4;
  const found = resolved.data?.found ? resolved.data : fromSearch;
  const entered = useRef(null);
  let t5;
  let t6;
  if ($[14] !== found || $[15] !== navigate) {
    t5 = () => {
      if (!found || entered.current === found.code) {
        return;
      }
      entered.current = found.code;
      enterUgcArticle(found, navigate, {
        replace: true
      });
    };
    t6 = [found, navigate];
    $[14] = found;
    $[15] = navigate;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  if (!isCode) {
    return "none";
  }
  if (found) {
    return "redirecting";
  }
  if (resolved.isPending) {
    return "checking";
  }
  return resolved.data && !resolved.data.found ? "notFound" : "none";
}
export function UgcNotFoundNotice(t0) {
  const $ = _c(13);
  const {
    code
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="grid size-10 shrink-0 place-items-center rounded-full bg-white text-ds-primary"><SearchX className="size-5" aria-hidden="true" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== code || $[2] !== t) {
    t2 = t("ugc.notFoundTitle", {
      code
    });
    $[1] = code;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <p className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-ds-ink">{t2}</p>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("ugc.notFound", {
      defaultValue: UGC_TEXTS.notFound
    });
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <p className="text-[14px] leading-[18px] text-ds-secondary">{t4}</p>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <output data-testid="ugc-not-found" className="flex items-start gap-3 rounded-[20px] bg-ds-quaternary p-4 md:items-center md:p-5">{t1}<div className="flex min-w-0 flex-col gap-1">{t3}{t5}</div></output>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}

/** Популярное вместо пустой выдачи - покупатель не остаётся в тупике. */
export function UgcSuggestions() {
  const $ = _c(12);
  const {
    t
  } = useTranslation();
  const city = usePreferences(_temp);
  let t0;
  if ($[0] !== city) {
    t0 = {
      queryKey: ["products", "list", "ugc-suggestions", city],
      queryFn: () => api.products.list({
        sort: "popular",
        pageSize: 12,
        city
      })
    };
    $[0] = city;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const popular = useQuery(t0);
  let t1;
  if ($[2] !== t) {
    t1 = t("ugc.suggestions");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <h2 className="type-h3-bold text-osmix-text-1">{t1}</h2>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== popular.data?.data || $[7] !== popular.isPending) {
    t3 = popular.isPending ? <ProductGridSkeleton count={4} /> : <DiscoverySourceProvider value="catalog"><ProductGrid products={popular.data?.data ?? []} /></DiscoverySourceProvider>;
    $[6] = popular.data?.data;
    $[7] = popular.isPending;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <section className="flex flex-col gap-4" data-testid="ugc-suggestions">{t2}{t3}</section>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp(s) {
  return s.city;
}